/**
 * Loads action field options from Firestore
 * `/configurations/pharmacy_order_updates/metadata/pa_requests`.
 */

import { useEffect, useMemo, useState } from "react";
import { FirestoreService } from "../../../api/firebase/firestoreService";
import { FirestoreDocumentReference } from "../../../api/firebase/references";
import {
  buildValueConfigFromPaRequestsMetadata,
  mapPaRequestsMetadataToFieldOptions,
  type PaRequestsMetadataDoc,
} from "../../../data-model/pharmacyOrderUpdatesPaRequestsMetadata";
import type {
  QueryBuilderConfig,
  QueryBuilderOption,
} from "../../../data-model/queryBuilder";

/**
 * Fetches pa_requests metadata and exposes field options for rule action modals.
 *
 * @returns Field options, optional value config from column top_values, and load state
 */
export function usePaRequestsActionFieldOptions() {
  const [metadata, setMetadata] = useState<PaRequestsMetadataDoc | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);

  useEffect(() => {
    let cancelled = false;

    const fetchMetadata = async () => {
      setLoading(true);
      setError(null);
      try {
        const path =
          FirestoreDocumentReference.pharmacyOrderUpdatesPaRequestsMetadata();
        const result =
          await FirestoreService.getDocument<PaRequestsMetadataDoc>(path);
        if (!cancelled) {
          setMetadata(result);
        }
      } catch (err) {
        const message = err instanceof Error ? err.message : String(err);
        if (message.includes("does not exist")) {
          if (!cancelled) setMetadata(null);
        } else {
          const errorInstance =
            err instanceof Error ? err : new Error(String(err));
          if (!cancelled) setError(errorInstance);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    fetchMetadata();
    return () => {
      cancelled = true;
    };
  }, []);

  const fieldOptions = useMemo(
    (): QueryBuilderOption[] => mapPaRequestsMetadataToFieldOptions(metadata),
    [metadata],
  );

  const valueConditionConfig = useMemo(
    (): QueryBuilderConfig => buildValueConfigFromPaRequestsMetadata(metadata),
    [metadata],
  );

  return {
    metadata,
    fieldOptions,
    valueConditionConfig,
    loading,
    error,
  };
}
